import { useEffect, useMemo, useState } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cardFill } from '../design/tokens';
import { useTheme } from '../design/useTheme';
import { useCatalog } from '../entity/catalogStore';
import { markWhereDiffers, tagMarkOf } from '../folders/TagMark';
import type { Arc } from '../plan/arc';
import { EMPTY_REGISTRIES, tagTint } from '../plan/registries';
import { usePlan } from '../plan/usePlan';
import { useVault } from '../session/useVault';
import { itemFor } from '../thoughts/corpus';
import { durationInRows } from '../thoughts/duration';
import { buildGathering } from '../thoughts/gathering';
import type { Gathering } from '../thoughts/gathering';
import { readThought } from '../thoughts/thought';
import type { Thought } from '../thoughts/thought';
import { ThoughtCard } from '../thoughts/ThoughtCard';
import type { CardShell } from '../thoughts/ThoughtCard';
import { parseEntity, transcriptTopics } from '../entity/entity';
import { recordingFace } from './nook';

const gatherings = new WeakMap<readonly Arc[], Gathering>();

function gatheringOf(arcs: readonly Arc[]): Gathering {
  let known = gatherings.get(arcs);
  if (!known) {
    known = buildGathering([...arcs]);
    gatherings.set(arcs, known);
  }
  return known;
}

/** What the card shows: a held topic by its own name, where it has one, and
 *  its own first words, rather than the whole recording's. */
function heldThought(path: string, text: string, anchor: string | undefined): Thought {
  const thought = readThought(path, text);
  if (!anchor) return thought;
  const topic = transcriptTopics(parseEntity(path, text)).find((t) => t.anchor === anchor);
  return {
    ...thought,
    title: topic && !topic.unnamed ? topic.title : null,
    face: [recordingFace(path, text, anchor)],
  };
}

/**
 * A recording or a note the nook holds, drawn as the Thoughts card — in its
 * panels, and a recording placed on its canvas: its own tint, its player and
 * length or its note glyph, its tag's mark where that is not the nook's, and
 * the day and time at its foot, since neither place has day heads.
 */
export function NookItemCard({
  path,
  anchor,
  kind,
  text,
  nookTags,
  placed = false,
  minimized,
  onExpand,
  onMore,
  shell,
  openProps,
  onOpen,
}: {
  path: string;
  anchor?: string;
  kind: 'recording' | 'note';
  text: string | null;
  /** The nook's own tags: a card marks only a tag the nook does not say. */
  nookTags: readonly string[];
  /** On the canvas already — said at its foot. */
  placed?: boolean;
  minimized?: boolean;
  onExpand?: () => void;
  onMore?: (at: { x: number; y: number }) => void;
  /** Held in the caller's own element, as a canvas card is. */
  shell?: (card: CardShell) => ReactNode;
  openProps?: ButtonHTMLAttributes<HTMLButtonElement>;
  onOpen: () => void;
}) {
  const { plan } = usePlan();
  const { state } = useVault();
  const catalog = useCatalog();
  const { theme } = useTheme();
  const registries = plan.status === 'ready' ? plan.plan.registries : EMPTY_REGISTRIES;
  const arcs = plan.status === 'ready' ? plan.plan.arcs : null;
  const thought = useMemo(() => (text ? heldThought(path, text, anchor) : undefined), [path, text, anchor]);

  // A newer recording keeps its length in its rows sidecar, a read of its own.
  const [durationMs, setDurationMs] = useState<number | null>(null);
  const rowsAt = kind === 'recording' ? (thought?.rowsAt ?? null) : null;
  useEffect(() => {
    if (!rowsAt || state.status !== 'ready') return;
    let cancelled = false;
    void state.vault
      .readFiles([rowsAt])
      .then(([file]) => {
        if (!cancelled && file) setDurationMs(durationInRows(file.text));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [rowsAt, state]);

  const tags = catalog.get(path)?.tags ?? [];
  const own = tagMarkOf(nookTags, registries.folders, theme);

  return (
    <ThoughtCard
      item={{ ...itemFor(path), kind }}
      thought={thought && { ...thought, durationMs: thought.durationMs ?? durationMs }}
      gatheredIn={arcs ? (gatheringOf(arcs).get(path) ?? []) : []}
      fill={cardFill(theme, tags[0] ? tagTint(registries, tags[0]) : null)}
      mark={markWhereDiffers(tagMarkOf(tags, registries.folders, theme), own)}
      onCanvas={placed}
      minimized={minimized}
      onExpand={onExpand}
      onMore={onMore}
      shell={shell}
      openProps={openProps}
      onOpen={onOpen}
    />
  );
}
